<template>
  <div class="advertising-channels">
    <div class="advertising-channels__body">
      <div class="advertising-channels__header">
        <h1 class="advertising-channels__title">Источники пациентов</h1>
        <button
          class="advertising-channels__button-add"
          type="button"
          v-if="showAddButton"
          v-on:click="onAddChannel">Добавить</button>
      </div>
      <input-checkbox
        title="Обязательное поле при создании нового пациента"
        v-model="isPatientSourceRequired"
        v-on:input="onRequiredFlagChanged"
      />
      <div
        v-for="item in list"
        v-bind:key="item.id">
        <v-text-field
          autocomplete="off"
          placeholder="Название"
          type="text"
          v-bind:autofocus="item.id === null"
          v-bind:hide-details="'auto'"
          v-bind:value="item.name"
          v-bind:error-messages="item.errors"
          v-on:blur="onSave(item, $event)">
          <template v-slot:append-outer>
            <button
              class="advertising-channels__icon-delete"
              type="button"
              v-if="showDelete"
              v-on:click="onDelete(item)" />
          </template>
        </v-text-field>
      </div>
    </div>
  </div>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import { AdvertisingChannel } from '@/storage/gateways/AdvertisingChannelGateway';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import InputCheckbox from '../../components/InputCheckbox.vue';

export default {
  components: { InputCheckbox },
  async created() {
    this.list = await this.fetchList();
  },
  computed: {
    showAddButton() {
      return this.actions.includes('create-channel');
    },
    showDelete() {
      return this.actions.includes('delete-channel');
    },
  },
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      isAwaitRequest: false,
      /**
       * @description Список рекламных каналов.
       * @type {AdvertisingChannel[]}
       */
      list: [],
      /**
       * @description Обязательность заполнения источника при создании нового пациента.
       * @type {bool}
       */
      isPatientSourceRequired: false,
    };
  },
  methods: {
    /**
     * @description Получить список рекламных каналов.
     * @returns {Promise<AdvertisingChannel[]>}
     */
    async fetchList() {
      const onFail = (error) => {
        this.notifyFail(error.title);
        return this.list;
      };
      const onSuccess = ({ actions, list, isRequired }) => {
        this.actions = actions;
        this.isPatientSourceRequired = isRequired;
        return list;
      };
      this.isAwaitRequest = true;
      const either = await this.$advertisingChannelGateway.viewList();
      this.isAwaitRequest = false;
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Добавить новый канал.
     * @returns {void}
     */
    onAddChannel() {
      this.list.unshift(new AdvertisingChannel(null, ''));
    },
    /**
     * @description Обработать создание рекламного канала.
     * @returns {void}
     */
    async onCreate(channel) {
      const onFail = (error) => {
        if (error.causes && error.causes.length > 0) {
          const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
          this.$set(channel, 'errors', Object.fromEntries(failurePairs).name);
        } else {
          this.notifyFail(error.title);
        }
      };
      const onSuccess = ({ message, id }) => {
        this.notifySuccess(message);
        this.$set(channel, 'id', id);
        this.$set(channel, 'errors', []);
      };
      this.isAwaitRequest = true;
      const either = await this.$advertisingChannelGateway.create(channel.name);
      this.isAwaitRequest = false;
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать удаление рекламного канала.
     * @param {AdvertisingChannel} channel Рекламный канал.
     * @returns {void}
     */
    async onDelete(channel) {
      if (channel.id === null && channel.name === '') {
        this.list = this.list.filter((item) => item !== channel);
        return;
      }
      const dialog = await this.showRemoveDialog('Вы действительно хотите удалить рекламный канал?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.list = this.list.filter((item) => item.id !== channel.id);
      };
      this.isAwaitRequest = true;
      const either = await this.$advertisingChannelGateway.delete(channel.id);
      this.isAwaitRequest = false;
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать редактирование рекламного канала.
     * @param {AdvertisingChannel} item Рекламный канал.
     * @returns {void}
     */
    async onEdit(channel) {
      const onFail = (error) => {
        if (error.causes && error.causes.length > 0) {
          const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
          this.$set(channel, 'errors', Object.fromEntries(failurePairs).name);
        } else {
          this.notifyFail(error.title);
        }
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.$set(channel, 'errors', []);
      };
      this.isAwaitRequest = true;
      const either = await this.$advertisingChannelGateway.edit(channel);
      this.isAwaitRequest = false;
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать сохранение рекламного канала.
     * @param {AdvertisingChannel} item  Рекламный канал.
     * @param {any}                event Событие.
     * @returns {void}
     */
    onSave(channel, event) {
      const newName = event.target.value;
      if (newName !== channel.name) {
        this.$set(channel, 'name', newName);
        if (channel.id) {
          this.onEdit(channel);
        } else {
          this.onCreate(channel);
        }
      }
    },
    /**
     * @description Обработать изменения флага.
     * Флаг обязательности заполнения источника нового пациента.
     */
    onRequiredFlagChanged() {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
      };
      this.isAwaitRequest = true;
      this.$advertisingChannelGateway
        .changePatientSourceRequiredFlag(this.isPatientSourceRequired)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
  },
  mixins: [builtInDialogMixin, notifierMixin],
  name: 'ClinicPanelAdvertisingChannelList',
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.advertising-channels {
  height: 100%;
  overflow-y: auto;
  width: 100%;
  &__body {
    flex-direction: column;
    display: flex;
    gap: 15px;
    max-width: 1187px;
    width: 100%;
    padding: 38px 20px 200px 23px;
  }
  &__header {
    display: flex;
    flex-flow: row wrap;
    gap: 10px;
    justify-content: space-between;
    margin: 0 0 40px 0;
  }
  &__button {
    &-add {
      @include buttons.btn-transparent(100%, 48px, 16px);
      @include buttons.btn-transparent-add(19px);
      max-width: 200px;
    }
  }
  &__icon-delete {
    background-image: url("@/assets/img/exit-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 12px;
    height: 12px;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/exit-blue.svg");
    }
  }
}
@media screen and (max-width: 640px) {
  .advertising-channels {
    &__body {
      padding: 38px 10px 140px;
    }
  }
}
</style>
